import { useT } from "@magic-md/i18n/react";
import { FileText, FolderOpen } from "lucide-react";
import type { ReactNode } from "react";
import { dirNameFromPath, fileNameFromPath } from "../../lib/path-utils";

export interface PublishTargetSummaryDetail {
  label: string;
  value: ReactNode;
  title?: string;
  mono?: boolean;
}

interface PublishTargetSummaryProps {
  kind: "document" | "folder";
  targetPath: string;
  publishPackagePath: string;
  details: PublishTargetSummaryDetail[];
}

export function PublishTargetSummary({
  kind,
  targetPath,
  publishPackagePath,
  details,
}: PublishTargetSummaryProps) {
  const t = useT("editor");
  const targetName = fileNameFromPath(targetPath) || targetPath;
  const parentPath = dirNameFromPath(targetPath);
  const isDocument = kind === "document";
  const Icon = isDocument ? FileText : FolderOpen;
  const title = isDocument ? t("publish.document") : t("publish.folder");
  const badge = isDocument ? t("publish.singleDocument") : t("publish.folderBadge");

  return (
    <section className="rounded-xl border border-border bg-card/70 p-3 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground">
          <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-medium text-muted-foreground">
              {title}
            </span>
            <span className="rounded-full border border-border bg-muted px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
              {badge}
            </span>
          </div>
          <div className="truncate text-sm font-medium text-foreground" title={targetName}>
            {targetName}
          </div>
          {parentPath && (
            <div className="break-all font-mono text-[10px] text-muted-foreground/80" title={targetPath}>
              {parentPath}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <SummaryDetail
          label={t("publish.package")}
          value={publishPackagePath}
          title={publishPackagePath}
          mono
        />
        {details.map((detail) => (
          <SummaryDetail
            key={detail.label}
            label={detail.label}
            value={detail.value}
            title={detail.title}
            mono={detail.mono}
          />
        ))}
      </div>
    </section>
  );
}

function SummaryDetail({
  label,
  value,
  title,
  mono,
}: PublishTargetSummaryDetail) {
  return (
    <div className="min-w-0 rounded-lg border border-border/70 bg-background px-2 py-1.5">
      <div className="mb-1 text-[9px] font-medium uppercase tracking-wide text-muted-foreground/70">
        {label}
      </div>
      <div
        className={`break-all text-[11px] text-foreground ${
          mono ? "font-mono" : ""
        }`}
        title={title}
      >
        {value}
      </div>
    </div>
  );
}
